import { useAssetHub } from '@/managers'
import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import {
	Button,
	ImageCropGuide,
	WidgetInputColorField,
	WidgetInputTextField,
	WidgetLabel,
	WidgetSelectDropdown,
	type WidgetSelectDropdownOption,
} from '../../../../rapid-ui'
import { logger } from '../../../../utilities'
import { externalCanvasDataAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Background, BackgroundMode } from '../../data/v1'
import { COLOR_REF_FULL_LABELS, COLOR_REF_MODES, ColorDot, type ColorRefMode } from './color-swatches'

export type BackgroundInspectorProps = {
	layerId: string
	value: Background
	onChange: (updates: Partial<Background>, options?: { transient?: boolean }) => void
}

export function BackgroundInspector({ layerId, value, onChange }: BackgroundInspectorProps) {
	const store = useCanvasStore()
	const externalData = useAtomValue(externalCanvasDataAtom, { store })
	const assetHub = useAssetHub()
	const prevModeRef = useRef(value.mode)
	const colorJustSelected = prevModeRef.current !== 'color' && value.mode === 'color'
	prevModeRef.current = value.mode

	// Measure the layer element for the crop guide
	const [layerSize, setLayerSize] = useState({ w: 0, h: 0 })
	useEffect(() => {
		const el = document.querySelector<HTMLElement>(`[data-layer-id="${CSS.escape(layerId)}"]`)
		if (!el) { return }
		const observer = new ResizeObserver(([entry]) => {
			setLayerSize({ w: entry.contentRect.width, h: entry.contentRect.height })
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [layerId])

	const externalColors: Record<ColorRefMode, string> = {
		foregroundColor: externalData.foregroundColor,
		backgroundColor: externalData.backgroundColor,
		accentForegroundColor: externalData.accentForegroundColor,
		accentBackgroundColor: externalData.accentBackgroundColor,
	}

	const options: WidgetSelectDropdownOption<BackgroundMode>[] = [
		{
			id: 'transparent',
			label: (
				<div className="flex items-center gap-2">
					<ColorDot />
					Transparent
				</div>
			),
		},
		...COLOR_REF_MODES.map((ref) => ({
			id: ref as BackgroundMode,
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={externalColors[ref] || '#ccc'} />
					{COLOR_REF_FULL_LABELS[ref]}
				</div>
			),
		})),
		{
			id: 'color' as BackgroundMode,
			label: (
				<div className="flex items-center gap-2">
					<ColorDot color={value.color} />
					Custom color
				</div>
			),
		},
		{
			id: 'image',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-3 rounded-full bg-grey-100 border border-grey-200 flex items-center justify-center shrink-0">
						<span className="text-[6px] font-semibold text-grey-400">IMG</span>
					</div>
					Image
				</div>
			),
		},
		{
			id: 'externalImage',
			label: (
				<div className="flex items-center gap-2">
					<div className="size-3 rounded-full bg-grey-100 border border-grey-200 flex items-center justify-center shrink-0">
						<span className="text-[6px] font-semibold text-grey-400">BG</span>
					</div>
					Background image
				</div>
			),
		},
	]

	const handlePickFromAssets = async () => {
		try {
			const asset = await assetHub.open()
			if (asset) {
				onChange({ imageUrl: asset.url })
			}
		} catch (error) {
			// The picker rejects with an AbortError when re-opened by another
			// caller — that's expected; anything else is a real failure worth
			// recording.
			if (error instanceof DOMException && error.name === 'AbortError') {
				return
			}
			logger.error(error, {
				tags: { component: 'BackgroundInspector', action: 'open-asset-hub' },
				data: { layerId },
			})
		}
	}

	return (
		<div className="flex flex-col gap-2">
			<WidgetLabel label="Background">
				<WidgetSelectDropdown
					options={options}
					value={value.mode}
					onChange={(mode) => onChange({ mode })}
					label="Background"
				/>
			</WidgetLabel>

			{value.mode === 'color' && (
				<WidgetInputColorField
					value={value.color}
					onChange={(color) => onChange({ color })}
					label="Color"
					initialOpen={colorJustSelected}
				/>
			)}

			{(value.mode === 'image' || value.mode === 'externalImage') && (
				<>
					{value.mode === 'image' && (
						<>
							<WidgetLabel label="Image URL">
								<WidgetInputTextField
									value={value.imageUrl}
									onChange={(imageUrl) => onChange({ imageUrl })}
									placeholder="Enter image URL..."
								/>
							</WidgetLabel>
							<Button size="short" type="committal" onClick={handlePickFromAssets}>
								Pick from assets
							</Button>
						</>
					)}
					{(() => {
						const src = value.mode === 'image'
							? value.imageUrl
							: value.mode === 'externalImage'
							? externalData.backgroundUrl
							: null
						if (!src) { return null }
						return (
							<ImageCropGuide
								src={src}
								imageScale={value.imageScale}
								imageOffsetX={value.imageOffsetX}
								imageOffsetY={value.imageOffsetY}
								containerWidth={layerSize.w}
								containerHeight={layerSize.h}
								width="fill"
								onScaleChange={(imageScale, transient) =>
									onChange({ imageScale }, transient ? { transient: true } : undefined)}
								onOffsetChange={(imageOffsetX, imageOffsetY, transient) =>
									onChange(
										{ imageOffsetX, imageOffsetY },
										transient ? { transient: true } : undefined,
									)}
							/>
						)
					})()}
				</>
			)}
		</div>
	)
}
